import { Input, Label } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  TRole,
  TSignupFormDataDE,
} from 'components/Signup/NewSignupFormDE/NewSignupFormDE.types';
import { useFormContext } from 'react-hook-form';
import signupFormStyles from '../../SignupForm/SignupForm.module.css';
import styles from './../PersonalDetailsDE.module.css';

type TProps = {
  roleType: TRole;
  addressIndex: number;
  personIndex: number;
  disabled: boolean;
};

export const AddressFieldsDE = ({
  roleType,
  addressIndex,
  personIndex,
  disabled,
}: TProps) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<TSignupFormDataDE>();

  const ADDRESS_PROPERTY =
    roleType === 'applicant'
      ? (`applicant.residentialAddresses.${addressIndex}` as const)
      : (`guarantors.${personIndex}.residentialAddresses.${addressIndex}` as const);

  const ERROR_PROPERTY =
    roleType === 'applicant'
      ? errors.applicant?.residentialAddresses?.[addressIndex]
      : errors?.guarantors?.[personIndex]?.residentialAddresses?.[addressIndex];

  return (
    <>
      <div className={signupFormStyles.formField}>
        <div className={styles.addressContainer}>
          <Input
            {...register(`${ADDRESS_PROPERTY}.street_line_1`)}
            className={styles.street}
            type="text"
            placeholder="z.B. Schildgasse"
            label={t`Street`}
            data-testid="address-street"
            disabled={disabled}
            aria-disabled={disabled}
            error={ERROR_PROPERTY?.street_line_1?.message}
            onBlur={(e) =>
              setValue(
                `${ADDRESS_PROPERTY}.street_line_1`,
                e.target.value.trim(),
              )
            }
          />
          <Input
            {...register(`${ADDRESS_PROPERTY}.house_number`)}
            type="text"
            placeholder="z.B. 21"
            label={t`House number`}
            data-testid={`${roleType}-house-number`}
            disabled={disabled}
            aria-disabled={disabled}
            error={ERROR_PROPERTY?.house_number?.message}
            onBlur={(e) =>
              setValue(
                `${ADDRESS_PROPERTY}.house_number`,
                e.target.value.trim(),
              )
            }
          />
        </div>
      </div>
      <div className={signupFormStyles.formField}>
        <Label
          className={styles.label}
          htmlFor={`${ADDRESS_PROPERTY}.street_line_2`}
        >
          <Trans>Address supplement</Trans>
          <span className={styles.optional}>({t`optional`})</span>
        </Label>
        <Input
          {...register(`${ADDRESS_PROPERTY}.street_line_2`)}
          id={`${ADDRESS_PROPERTY}.street_line_2`}
          type="text"
          data-testid="address-supplement"
          placeholder="z.B. Apt 2"
          disabled={disabled}
          aria-disabled={disabled}
          error={ERROR_PROPERTY?.street_line_2?.message}
          onBlur={(e) =>
            setValue(`${ADDRESS_PROPERTY}.street_line_2`, e.target.value.trim())
          }
        />
      </div>
      <div className={signupFormStyles.formField}>
        <div className={styles.addressContainer}>
          <Input
            {...register(`${ADDRESS_PROPERTY}.postcode`)}
            maxLength={5}
            wrapperClassName={styles.postcode}
            type="text"
            placeholder="z.B. 37269"
            label={t`Postcode`}
            data-testid={`${roleType}-postcode`}
            disabled={disabled}
            aria-disabled={disabled}
            error={ERROR_PROPERTY?.postcode?.message}
            onBlur={(e) =>
              setValue(`${ADDRESS_PROPERTY}.postcode`, e.target.value.trim())
            }
          />
          <Input
            {...register(`${ADDRESS_PROPERTY}.town`)}
            type="text"
            wrapperClassName={styles.town}
            placeholder="z.B. Waldmünchen"
            label={t`Town`}
            data-testid={`${roleType}-town`}
            disabled={disabled}
            aria-disabled={disabled}
            error={ERROR_PROPERTY?.town?.message}
            onBlur={(e) =>
              setValue(`${ADDRESS_PROPERTY}.town`, e.target.value.trim())
            }
          />
        </div>
      </div>
    </>
  );
};
